import React, { useState } from 'react';
import { education, certifications, personalInfo } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  gray: '#4a6275', white: '#e8f4f8',
  border: 'rgba(0,245,255,0.12)',
  card: 'rgba(0,245,255,0.025)',
};

const issuerColors = {
  'Google': '#4285F4',
  'TensorFlow / Google': '#FF6F00',
  'DeepLearning.AI · Coursera': '#7b2fff',
  'Bangkit Academy': '#00f5ff',
  'Dicoding Indonesia': '#00ff88',
};

export default function Education() {
  const [hovEdu, setHovEdu] = useState(null);
  const [hovCert, setHovCert] = useState(null);

  return (
    <section id="education">
      <div style={{ marginBottom: 56 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: "'Space Mono', monospace", fontSize: 10,
          letterSpacing: 4, textTransform: 'uppercase', color: C.cyan, marginBottom: 12,
        }}>
          <span style={{ width: 32, height: 1, background: C.cyan, display: 'inline-block' }} />
          Education
        </div>
        <h2 style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 'clamp(26px, 4vw, 44px)',
          fontWeight: 700, color: C.white, letterSpacing: -1,
        }}>
          Academic <span style={{ color: C.cyan }}>Background</span>
        </h2>
        <p style={{ fontSize: 14, color: C.gray, marginTop: 10, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>
          Click any card to view transcript
        </p>
      </div>

      {/* Education Cards — clickable to Google Drive */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
        gap: 20, marginBottom: 64,
      }}>
        {education.map((edu, i) => {
          const isHov = hovEdu === i;
          return (
            <a
              key={i}
              href={personalInfo.transcriptDrive}
              target="_blank"
              rel="noreferrer"
              onMouseEnter={() => setHovEdu(i)}
              onMouseLeave={() => setHovEdu(null)}
              style={{
                display: 'block',
                textDecoration: 'none',
                background: edu.highlight ? 'rgba(0,245,255,0.04)' : C.card,
                border: `1px solid ${isHov ? 'rgba(0,245,255,0.5)' : edu.highlight ? 'rgba(0,245,255,0.25)' : C.border}`,
                padding: 28, transition: 'all .3s',
                transform: isHov ? 'translateY(-6px)' : 'translateY(0)',
                boxShadow: isHov ? '0 24px 56px rgba(0,0,0,0.4), 0 0 20px rgba(0,245,255,0.08)' : edu.highlight ? '0 0 20px rgba(0,245,255,0.06)' : 'none',
                position: 'relative', overflow: 'hidden',
                cursor: 'pointer',
              }}
            >
              {edu.highlight && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0,
                  height: 2,
                  background: `linear-gradient(90deg, ${C.cyan}, ${C.purple})`,
                }} />
              )}

              {/* View transcript badge on hover */}
              {isHov && (
                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  fontFamily: "'Space Mono', monospace", fontSize: 8,
                  letterSpacing: 2, color: C.cyan,
                  border: '1px solid rgba(0,245,255,0.4)',
                  padding: '3px 10px', textTransform: 'uppercase',
                  background: 'rgba(0,245,255,0.06)',
                }}>
                  View Transcript ↗
                </div>
              )}

              <div style={{ fontSize: 28, marginBottom: 14 }}>{edu.icon}</div>

              <div style={{
                fontFamily: "'Space Mono', monospace", fontSize: 9,
                letterSpacing: 2, color: edu.highlight ? C.green : C.cyan,
                textTransform: 'uppercase', marginBottom: 10,
              }}>
                {edu.period}
              </div>

              <h3 style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: 15, fontWeight: 600, color: C.white,
                marginBottom: 8, lineHeight: 1.4,
              }}>
                {edu.degree}
              </h3>

              <div style={{ fontSize: 13, color: C.gray, marginBottom: 16, lineHeight: 1.6 }}>
                {edu.school}
              </div>

              {edu.gpa && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontFamily: "'Space Mono', monospace", fontSize: 10,
                  letterSpacing: 1.5, padding: '5px 14px',
                  border: `1px solid rgba(0,255,136,0.3)`,
                  color: C.green,
                }}>
                  {edu.gpa.includes('/') ? `GPA: ${edu.gpa}` : `🏆 ${edu.gpa}`}
                </div>
              )}

              {edu.note && (
                <p style={{
                  marginTop: 14, lineHeight: 1.7,
                  color: 'rgba(232,244,248,0.45)',
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 10.5,
                }}>
                  {edu.note}
                </p>
              )}
            </a>
          );
        })}
      </div>

      {/* Certifications */}
      <div>
        <div style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 20, fontWeight: 600, color: C.white,
          marginBottom: 24, letterSpacing: 1,
        }}>
          Certifications & <span style={{ color: C.cyan }}>Achievements</span>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 14,
        }}>
          {certifications.map((cert, i) => {
            const isHov = hovCert === i;
            const accentColor = issuerColors[cert.issuer] || C.cyan;
            return (
              <a
                key={i}
                href={cert.link || '#'}
                target="_blank"
                rel="noreferrer"
                onMouseEnter={() => setHovCert(i)}
                onMouseLeave={() => setHovCert(null)}
                style={{
                  display: 'flex',
                  textDecoration: 'none',
                  background: C.card,
                  border: `1px solid ${isHov ? accentColor + '55' : C.border}`,
                  padding: '20px 22px',
                  alignItems: 'center', gap: 16,
                  transition: 'all .3s',
                  transform: isHov ? 'translateX(6px)' : 'translateX(0)',
                  cursor: 'pointer',
                  position: 'relative',
                  boxShadow: isHov ? `0 0 20px ${accentColor}18` : 'none',
                }}
              >
                {/* Accent line left */}
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: 3,
                  background: isHov ? accentColor : 'transparent',
                  transition: 'background .3s',
                }} />
                <div style={{
                  width: 44, height: 44,
                  background: isHov ? accentColor + '18' : 'rgba(0,245,255,0.06)',
                  border: `1px solid ${isHov ? accentColor + '44' : C.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, flexShrink: 0, transition: 'all .3s',
                }}>
                  {cert.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: C.white, marginBottom: 5, lineHeight: 1.4 }}>
                    {cert.name}
                  </div>
                  <div style={{
                    fontFamily: "'Space Mono', monospace", fontSize: 9,
                    letterSpacing: 1.5, color: isHov ? accentColor : C.gray,
                    textTransform: 'uppercase', transition: 'color .3s',
                  }}>
                    {cert.issuer} · {cert.year}
                  </div>
                </div>
                {isHov && (
                  <span style={{ color: accentColor, fontSize: 16, flexShrink: 0 }}>↗</span>
                )}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
